import { useEffect, useState } from 'react';

import { groupBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';

import { BaseSelectFilter } from '@/components/common';

import { useGetTradelanesQuery } from '@/graphql/GetTradelanes.generated';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import {
  TradelanesFilter,
  getTradelane,
  selectTradelanes,
  setSelectedTradelanes,
  setTradelanes,
  store,
  unselectAllTradelanes,
  unselectTradelane,
  unselectTradelanes,
} from '../store';

function TradelanesSelectFilter() {
  const appSnap = useSnapshot(appStore);
  const snap = useSnapshot(store);
  const { t } = useTranslation();
  const [{ data, fetching, error }, reexecuteQuery] = useGetTradelanesQuery({
    pause: snap.tradelanes.length > 0,
  });

  const [errorId, setErrorId] = useState<string | null>(null);

  useEffect(() => {
    if (data && data.glec_tradelanes) {
      setTradelanes(data.glec_tradelanes);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const handleRemove = (id: string) => () => unselectTradelane(id);

  const handleSelectGroup = (tradelanes: TradelanesFilter[]) => () =>
    selectTradelanes(tradelanes.map((tradelane) => tradelane.id));

  const handleClearGroup = (tradelanes: TradelanesFilter[]) => () =>
    unselectTradelanes(tradelanes.map((tradelane) => tradelane.id));

  const groups = groupBy(snap.tradelanes, 'left');

  const selectedTradelanesIds = snap.selectedTradelanes.map(
    (tradelane) => tradelane.id,
  );

  return (
    <BaseSelectFilter
      id="tradelanes-select-filter"
      label={t('tradelanes')}
      values={selectedTradelanesIds}
      onChange={setSelectedTradelanes}
      onClear={unselectAllTradelanes}
      loading={fetching}
      error={
        appSnap.hasRequestError(errorId)
          ? 'Error while fetching tradelanes'
          : null
      }
      onRetry={handleRetry}
      maxHeight={48 * 8 + 8}
      renderChips={(selected: string[]) =>
        selected
          .map(getTradelane)
          .filter((tradelane) => tradelane !== null)
          .map((tradelane) => (
            <Chip
              key={tradelane.id}
              color="primary"
              variant="outlined"
              label={
                tradelane.right
                  ? `${tradelane.left} to ${tradelane.right}`
                  : tradelane.label
              }
              onDelete={handleRemove(tradelane.id)}
            />
          ))
      }
    >
      {Object.entries(groups).map(([group, tradelanes]) => {
        const allSelected = tradelanes.every((tradelane) => tradelane.selected);
        return [
          <ListSubheader
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {group}
              <sup
                style={{
                  marginLeft: 4,
                  visibility: tradelanes.some((tradelane) => tradelane.selected)
                    ? 'visible'
                    : 'hidden',
                }}
              >
                ({tradelanes.filter((tradelane) => tradelane.selected).length})
              </sup>
            </Box>
            <Button
              sx={{ textTransform: 'none' }}
              onClick={
                allSelected
                  ? handleClearGroup(tradelanes)
                  : handleSelectGroup(tradelanes)
              }
            >
              {allSelected ? `Clear` : `Select all`}
            </Button>
          </ListSubheader>,
          tradelanes
            .filter((t) => typeof t !== 'undefined')
            .map((tradelane) => (
              <MenuItem key={tradelane.id} value={tradelane.id}>
                <Checkbox checked={tradelane.selected} />
                <ListItemText
                  primary={
                    tradelane.right ? `to ${tradelane.right}` : tradelane.label
                  }
                />
              </MenuItem>
            )),
        ];
      })}
    </BaseSelectFilter>
  );
}

export { TradelanesSelectFilter };
